import React, { useEffect, useReducer, useState } from 'react';
import { Divider, Stack, Typography } from '@mui/material';
import TextInput from 'components/Common/TextInput';
import Button from 'components/Common/Button';
import RadioButton from 'components/Common/RadioGroup';
import ChangePhoto from 'pages/Account/ChangePhoto';
import BasketPickerAndDateItem from 'components/Home/BasketPickerAndDateItem';
import { addNewBasketData } from 'services/addNewBasketData';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import routes from 'constants/routes';
import { removeBasketImage, updateBasketDetail, updateBasketImage } from 'services/updateBasketDetail';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import { convertToISOString, isDateAfter } from 'utils/helpers';
import { notify } from 'components/Common/Toast';
import ErrorMessage from 'components/Common/ErrorMessage';
import { fetchBasketDetail } from 'redux/slices/baskets/basketDetailsSlice';
import MuiLoader from 'components/Common/Loader';
import CardWrapper from 'components/Common/CardWrapper';
import { Basket } from 'types/common';
import { goalAmount } from 'appConfig';

type FormKeys = keyof typeof defaultValues;

const defaultValues = {
    name: '',
    description: '',
    subType: 'GIVING',
    startDate: '',
    nextPlannedStatusDate: '',
    nextPlannedStatus: 'SUSPEND',
    // timeZone: "America/Chicago",
    // estimatedContributors: 0,
    // currencyCode: "840",
    type: 'OPEN',
    goalAmount,
};
const ACTIVE_IMMEDIATELY = '@catholic/ACTIVE_IMMEDIATELY';
const ACTIVE_SCHEDULED = '@catholic/ACTIVE_SCHEDULED';
const SUSPEND_SCHEDULED = '@catholic/SUSPEND_SCHEDULED';
const SUSPEND_MANUAL = '@catholic/SUSPEND_MANUAL';
const END_DATE_ERROR = '@catholic/END_DATE_ERROR';
const NEXT_PLANNED_STATUS_DATE_ERROR = '@catholic/NEXT_PLANNED_STATUS_DATE_ERROR';
const START_DATE_ERROR = '@catholic/START_DATE_ERROR';
const NAME_ERROR = '@catholic/NAME_ERROR';

interface DateErrorTypes {
    endDate: boolean;
    nextPlannedStatusDate: boolean;
    startDate: boolean;
    title: boolean;
}

interface CreateBasketSectionProps {
    title: string;
    children: React.ReactNode;
}

const initialDateErrorState: DateErrorTypes = {
    endDate: false,
    nextPlannedStatusDate: false,
    startDate: false,
    title: false,
};

const dateErrorReducer = (state: DateErrorTypes, action: any) => {
    switch (action.type) {
        case END_DATE_ERROR:
            return {
                ...state,
                endDate: action.payload,
            };
        case NEXT_PLANNED_STATUS_DATE_ERROR:
            return {
                ...state,
                nextPlannedStatusDate: action.payload,
            };
        case START_DATE_ERROR:
            return {
                ...state,
                startDate: action.payload,
            };
        case NAME_ERROR:
            return {
                ...state,
                title: action.payload,
            };
        default:
            return state;
    }
};

const Section = ({ title, children }: CreateBasketSectionProps) => {
    return (
        <Stack width={'100%'} mt={4}>
            <Typography sx={{ fontSize: 16, mb: 2, fontWeight: 400, ml: { xs: 2, md: 8 } }} variant="h1">
                {title}
            </Typography>
            <Divider />
            <Stack gap={1} mx={{ xs: 4, md: 16 }} my={2}>
                {children}
            </Stack>
        </Stack>
    );
};

function CreateBasket() {
    //TODO: NEED TO FIX THE IS ARCHDIOCESE VARIABLE BASED ON THE ROLE
    const isArchdiocese = false;
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [loading, setLoading] = useState<boolean>(false);
    const [imgValue, setImgValue] = useState('');
    const [imgLoading, setImgLoading] = useState(false);
    const [activationType, setActivationType] = useState<string>(ACTIVE_IMMEDIATELY);
    const [suspendType, setSuspendType] = useState<string>(SUSPEND_MANUAL);
    const { basketDetails, loading: basketDetailLoading } = useSelector((state: RootState) => state?.baskets?.details);
    const { userInfo, defaultCompanyId } = useSelector((state: RootState) => state.user);
    const [dateError, dateErrorDispatch] = useReducer(dateErrorReducer, initialDateErrorState);
    const { firstName, lastName } = userInfo || {};
    const { id } = useParams();
    const { handleSubmit, setValue, getValues, watch } = useForm({
        defaultValues: { ...defaultValues, firstName, lastName },
    });
    const disableActivationDate = activationType === ACTIVE_IMMEDIATELY;
    const disableSuspendDate = suspendType === SUSPEND_MANUAL;
    const basketStatus = basketDetails?.status;

    useEffect(() => {
        if (Number(id) === basketDetails?.id) {
            const { imgUrl, startDate, nextPlannedStatusDate } = basketDetails || {};
            imgUrl && setImgValue(imgUrl);
            setFormValuesFromBasket(basketDetails);
            if (isDateAfter(startDate)) {
                setActivationType(ACTIVE_SCHEDULED);
            }
            if (nextPlannedStatusDate) {
                setValue('nextPlannedStatus', 'SUSPEND');
                setSuspendType(SUSPEND_SCHEDULED);
            }
        } else if (id && Number(id) !== basketDetails?.id) {
            dispatch(fetchBasketDetail(id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, basketDetails]);

    const setFormValuesFromBasket = (basket: Basket) => {
        (Object.keys(defaultValues) as FormKeys[]).forEach((key) => {
            const value = basket[key as keyof Basket];
            if (typeof value === 'string' || typeof value === 'number') {
                setValue(key, value);
            } else {
                // Handle other types if necessary, e.g., nested objects or null values
                setValue(key, defaultValues[key]);
            }
        });
    };
    const { nextPlannedStatusDate, startDate, name } = getValues();

    const checkDateAndTimeError = (date: string | Date): boolean => {
        if (!date) return true;
        return dayjs(convertToISOString(date)).isBefore(dayjs(convertToISOString('')));
    };

    const handleError = (type: string, value: string | Date) => {
        let error = false;
        if (type === NAME_ERROR) {
            error = !value;
        } else {
            error = checkDateAndTimeError(value);
        }
        dateErrorDispatch({ type, payload: error });
    };

    const updateBasket = async (payload: any) => {
        // CHECKING FOR STRING BECAUSE IF IT'S STRING THEN IMAGE IS NOT UPDATED BY THE USER
        if (imgValue && typeof imgValue !== 'string' && id) {
            try {
                setImgLoading(true);
                const res: any = await updateBasketImage(imgValue, id);
                if (res.code === 200) {
                    dispatch(fetchBasketDetail(id));
                }
                setImgLoading(false);
            } catch (e) {
                setImgLoading(false);
            }
        } else if (basketDetails?.imgUrl && !imgValue && id) {
            try {
                setImgLoading(true);
                const res: any = await removeBasketImage(id);
                if (res.code === 200) {
                    dispatch(fetchBasketDetail(id));
                }
                setImgLoading(false);
            } catch (e) {
                setImgLoading(false);
            }
        }
        try {
            setLoading(true);
            const responseEditBasket: any = await updateBasketDetail(id, payload);
            if (responseEditBasket?.code === 200) {
                notify('Basket updated successfully!', 'success');
                id && navigate(routes.home.basketDetails.replace(':id', id.toString()), { replace: true });
            }
        } catch (error) {
            console.log('edit basket error', error);
        } finally {
            setLoading(false);
        }
    };

    const createBasket = async (payload: any) => {
        try {
            setLoading(true);
            const response = await addNewBasketData({ ...payload, companyId: defaultCompanyId });
            if (response?.code === 200) {
                notify('Basket created successfully!', 'success');
                navigate(routes.home.basketDetails.replace(':id', response?.id.toString()), { replace: true });
                return response;
            } else {
                console.log('create basket response code not 200 : ', response);
            }
        } catch (error: any) {
            console.log('create basket error', error);
        } finally {
            setLoading(false);
        }
    };

    const onFormSubmit = async () => {
        if (!name) {
            return handleError(NAME_ERROR, name);
        }
        if (activationType === ACTIVE_SCHEDULED && !startDate) {
            return handleError(START_DATE_ERROR, startDate);
        }
        if (suspendType === SUSPEND_SCHEDULED && !nextPlannedStatusDate) {
            return handleError(NEXT_PLANNED_STATUS_DATE_ERROR, nextPlannedStatusDate);
        }
        const formValues: any = getValues();
        if (activationType === ACTIVE_IMMEDIATELY) {
            formValues.startDate = convertToISOString(new Date());
        }
        if (suspendType === SUSPEND_MANUAL) {
            delete formValues.nextPlannedStatus;
            delete formValues.nextPlannedStatusDate;
        } else {
            formValues.nextPlannedStatus = 'SUSPEND';
        }
        if (id) {
            updateBasket(formValues);
        } else {
            createBasket(formValues);
        }
    };
    const handleActivationDate = (date: string) => {
        setValue('startDate', date);
        if (activationType === ACTIVE_IMMEDIATELY) return;
        handleError(START_DATE_ERROR, date);
    };
    const handleSuspendDate = (date: string) => {
        setValue('nextPlannedStatusDate', date);
        if (suspendType === SUSPEND_MANUAL) return;
        handleError(NEXT_PLANNED_STATUS_DATE_ERROR, date);
    };
    const handleTitleChange = (key: FormKeys, value: string) => {
        setValue(key, value);
        handleError(NAME_ERROR, value);
    };

    return (
        <CardWrapper cardProps={{ sx: { p: 0 } }}>
            <MuiLoader absolute loading={basketDetailLoading} />
            <form onSubmit={handleSubmit(onFormSubmit)}>
                <Section title="Basket Details">
                    <TextInput
                        name="name"
                        value={watch('name')}
                        setValue={handleTitleChange}
                        label="Title"
                        icon={null}
                        sx={{ mt: 2 }}
                        inputProps={{ maxLength: 255 }}
                        width="100%"
                        placeholder="Type Basket title"
                    />
                    <div>{dateError?.title ? <ErrorMessage message="Title is required" /> : null}</div>
                    {/* <TextInput type="number" label="Contact" icon={null} sx={{ mt: 2 }} width="100%" placeholder="type number only for testing" /> */}
                    <TextInput
                        name="description"
                        value={watch('description')}
                        setValue={setValue}
                        label="Basket Text"
                        icon={null}
                        sx={{ mt: 2, mb: 18, p: 0 }}
                        multiline
                        minRows={4}
                        maxRows={4}
                        inputProps={{ maxLength: 255 }}
                        width="100%"
                        placeholder="Type text here..."
                    />
                </Section>
                <Section title="Basket Type">
                    <div style={{ marginBottom: 18, marginTop: 18 }}>
                        <RadioButton
                            alignItems="flex-start"
                            data={[
                                { value: 'GIVING', label: 'Giving Basket (Tax-deductible contributions)' },
                                { value: 'PAYMENT', label: 'Payment Basket (Payment for goods or services)', defaultValue: 'GIVING' },
                            ]}
                            value={watch('subType')}
                            name="subType"
                            setValue={(value: string) => setValue('subType', value)}
                            disabled={!!id}
                            sx={{ gap: { xs: 2, sm: 0 } }}
                        />
                    </div>
                </Section>
                {id && (
                    <ChangePhoto
                        isLoading={imgLoading}
                        imgValue={imgValue}
                        setImgValue={setImgValue}
                        tabProps={{ sx: { mx: { md: 8 } } }}
                        stackProps={{ mx: { md: 14 } }}
                    />
                )}
                <br />

                <Section title="Activation Details">
                    <BasketPickerAndDateItem
                        data={[
                            { value: ACTIVE_IMMEDIATELY, label: 'Activate Immediately' },
                            { value: ACTIVE_SCHEDULED, label: 'Scheduled Activation' },
                        ]}
                        setDate={handleActivationDate}
                        date={watch('startDate')}
                        type={activationType}
                        setType={setActivationType}
                        name="activation"
                        disableRadio={!!id && basketStatus !== 'PENDING'}
                        disableDate={disableActivationDate}
                        disableTime={disableActivationDate}
                        defaultNull={disableActivationDate}
                    >
                        <div>{dateError.startDate ? <ErrorMessage /> : null}</div>
                    </BasketPickerAndDateItem>
                </Section>
                <Section title="Suspend Details">
                    <BasketPickerAndDateItem
                        data={[
                            { value: SUSPEND_MANUAL, label: 'Manual Suspend' },
                            { value: SUSPEND_SCHEDULED, label: 'Scheduled Suspend' },
                        ]}
                        date={watch('nextPlannedStatusDate')}
                        setDate={handleSuspendDate}
                        setType={setSuspendType}
                        type={suspendType}
                        name="suspend"
                        disableDate={disableSuspendDate}
                        disableTime={disableSuspendDate}
                        defaultNull={disableSuspendDate}
                    >
                        <div>{dateError.nextPlannedStatusDate ? <ErrorMessage /> : null}</div>
                    </BasketPickerAndDateItem>
                </Section>
                {isArchdiocese && (
                    <Section title="Archdiocese Settings">
                        <Stack ml={-4}>
                            <Typography variant="body1">
                                Sherlock Holmes had opened his mouth to reply, when the door flew open, and Peterson, the commissionaire,
                                rushed into the apartment with flushed cheeks and the face of a man who is dazed with astonishment.
                            </Typography>
                        </Stack>
                        <div style={{ marginBottom: 18, marginTop: 18 }}>
                            <RadioButton
                                alignItems="flex-start"
                                data={[
                                    { value: 'specific', label: 'Archdiocese specific' },
                                    { value: 'wide', label: 'Archdiocese wide' },
                                ]}
                                value="specific"
                            />
                        </div>
                    </Section>
                )}

                <Stack maxWidth={{ xs: '250px', sm: '372px' }} m="auto" sx={{ mt: 2, mb: 4, px: 1 }}>
                    <Button width="100%" title="Save" loading={loading || imgLoading} type="submit" />
                </Stack>
            </form>
        </CardWrapper>
    );
}

export default CreateBasket;
